import { useEffect, useState } from "react";
import "./style.scss";
import RadioButtonUI from "@UI/RadioButtonUI";
import InputNumberUI from "@UI/InputNumberUI";
import { Button } from "primereact/button";
import { inject, observer } from "mobx-react";
import TagUi from "@UI/TagUi";
import { pluralForm } from "../../../../helpers/utils";
import { confirmDialog } from "primereact/confirmdialog";
import SelectSuggestUI from "../../../../UI/SelectSuggestUI";

const { VITE_REDIRECT_URL } = import.meta.env;

const LegoItem = inject("mainStore")(
    observer(({ mainStore, images, keyToGet }) => {
        const sites = mainStore.getMainStore("suggest.sites_suggest") || [];
        const isAi = ["ai", "categoria"].includes(keyToGet);

        const defaultConfig = {
            bgColor: "default",
            ctaColor: "default",
            queueDuration: isAi ? 20 : 6,
            queueRandomness: true,
            isPartnerHeaderVariant: false,
        };

        const [data, setData] = useState([]);
        const [configData, setConfigData] = useState(false);
        const [queueDurationMode, setQueueDurationMode] = useState(defaultConfig.queueDuration);
        const [vitrinaById, setVitrinaById] = useState({});

        useEffect(() => {
            getData();
        }, []);

        const getData = async () => {
            const resp = await mainStore.getByUrl(`/abtest/legoConfiguration`, {
                filters: {
                    template: keyToGet,
                    status: "active|draft|archived",
                },
            });
            setData(resp.data.reverse());
        };

        const assignParameters = () => {
            if (configData) {
                setConfigData(false);
                return;
            }
            setConfigData(defaultConfig);
            setQueueDurationMode(defaultConfig.queueDuration);
        };

        const getNextVersion = (items) => {
            const lastVersion = items?.[0]?.version;
            if (!lastVersion) {
                return "1_0";
            }

            const [major, minor] = lastVersion.split("_");
            const nextMajor = Number.parseInt(major);
            if (Number.isNaN(nextMajor)) {
                return "1_0";
            }

            return `${nextMajor + 1}_${minor || "0"}`;
        };

        const handleSave = async () => {
            const nextVersion = getNextVersion(data);
            const code = `${keyToGet}_${nextVersion}`;
            const [nextMajor] = nextVersion.split("_");
            const title = `${keyToGet}_${nextMajor || "1"}`;

            const resp = await mainStore.postByUrl(`/abtest/legoConfiguration`, { template: keyToGet, templateConfig: configData, title, code, version: nextVersion });

            if (resp.success) {
                setConfigData(false);
                getData();
            }
        };

        const handlePreview = (item) => {
            const what_id = getVitrinaValue(item._id)._id;
            window.open(`${VITE_REDIRECT_URL}/?what_id=${what_id}&widgets=${item.code}`);
        };

        const radioData = [
            {
                title: "Фон очереди",
                key: "bgColor",
                data: [
                    { label: "Темный", value: "default" },
                    { label: "Однотонный из палитры", value: "palette" },
                ],
            },
            {
                title: "Цвет кнопки на витрину",
                key: "ctaColor",
                data: [
                    { label: "Из палитры", value: "palette" },
                    { label: "Зеленый", value: "default" },
                ],
            },
            {
                title: "Длина очереди",
                key: "queueDuration",
                data: [
                    { label: isAi ? "20 секунд" : "6 секунд", value: isAi ? 20 : 6 },
                    {
                        label: (
                            <InputNumberUI
                                placeholder="Введите значение"
                                disabled={queueDurationMode !== "custom"}
                                value={queueDurationMode === "custom" ? configData?.queueDuration : null}
                                onChange={(e) => setConfigData({ ...configData, queueDuration: isAi ? e.value : e.value > 9 ? 9 : e.value < 1 ? 1 : e.value })}
                                max={isAi ? 99 : 9}
                                maxLength={isAi ? 2 : 1}
                                min={1}
                            />
                        ),
                        value: "custom",
                    },
                ],
            },
            {
                title: "Количество подарков",
                key: "queueRandomness",
                data: [
                    { label: "Рандомные значения", value: true },
                    { label: "Близкие значения", value: false },
                ],
            },
            {
                title: "Содержания H1",
                key: "isPartnerHeaderVariant",
                data: [
                    { label: "Стандартный", value: false },
                    { label: "Стандартный+Partners name", value: true },
                ],
            },
        ];

        const getTemplateDisplayValue = (key, value) => {
            const info = templateInfo[key];
            if (typeof info === "function") {
                return info(value);
            }

            return info[value] || "";
        };

        const handleDelete = (id) => {
            confirmDialog({
                message: "Проверьте нет ли показов по нему",
                header: "Вы уверены, что хотите архивировать виджет?",
                acceptLabel: "В архив",
                rejectLabel: "Отмена",
                acceptClassName: "p-button-danger",
                className: "lego-item__confirm-dialog",

                accept: async () => {
                    const resp = await mainStore.putByUrl(`/abtest/legoConfiguration/${id}`, { status: "archived" });
                    if (resp.success) {
                        getData();
                    }
                },
            });
        };

        const getVitrinaValue = (id) => vitrinaById[id] || null;
        const setVitrinaValue = (id, value) => {
            setVitrinaById((prev) => ({ ...prev, [id]: value }));
        };

        return (
            <div className="lego-item">
                <div className="lego-item__imgs">
                    {images.map((img, index) => (
                        <img key={index} src={img} alt={`Lego item ${index}`} />
                    ))}
                </div>

                <div className="lego-item__options">
                    <div className="lego-item__assign" onClick={assignParameters}>
                        <span>Назначить параметры</span>
                        <i className={`pi pi-${configData ? "minus-circle" : "plus-circle"}`}></i>
                    </div>
                    {configData && (
                        <>
                            {radioData.map((item, idx) => {
                                const value = item.key === "queueDuration" ? queueDurationMode : configData[item.key];
                                const onChange =
                                    item.key === "queueDuration"
                                        ? (e) => {
                                              setQueueDurationMode(e.value);
                                              if (e.value !== "custom") {
                                                  setConfigData({ ...configData, queueDuration: e.value });
                                              }
                                          }
                                        : (e) => setConfigData({ ...configData, [item.key]: e.value });

                                return <RadioButtonUI key={idx} addClass="lego-item__option" label={idx + 1 + "." + item.title} radioData={item.data} value={value} onChange={onChange} />;
                            })}
                            <Button label="Сохранить виджет" className="lego-item__save-btn" severity="success" onClick={handleSave} />
                        </>
                    )}
                </div>
                {data?.length > 0 &&
                    data.map((item, index) => {
                        if (item.status === "archived") return null;
                        return (
                            <div key={index} className="lego-item__data">
                                <div className="lego-item__data-header">
                                    <span>{item.title}</span>
                                    <div className="lego-item__tags">
                                        <TagUi value={statusMap[item.status].label} severity={statusMap[item.status].severity} className={statusMap[item.status].severity} />
                                        <TagUi value={item._id} className="gray" />
                                    </div>
                                </div>
                                <div className="lego-item__info">
                                    {radioData.map((r, rIDx) => {
                                        const displayValue = getTemplateDisplayValue(r.key, item.templateConfig[r.key]);
                                        return (
                                            <div key={rIDx} className="lego-item__info-item">
                                                <span className="lego-item__info-label">{rIDx + 1 + "." + r.title}</span>
                                                <span>{displayValue}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="lego-item__vitrina">
                                    <SelectSuggestUI options={sites} label="Выберите витрину, чтобы сгенерировать превью" value={getVitrinaValue(item._id)} onChange={(e) => setVitrinaValue(item._id, e.target.value)} />
                                    <Button label="Посмотреть превью виджета" icon="pi pi-images" severity="help" outlined disabled={!getVitrinaValue(item._id)} onClick={() => handlePreview(item)} />
                                </div>
                                <Button label="В архив" icon="pi pi-trash" className="lego-item__delete-btn" severity="danger" iconPos="right" outlined onClick={() => handleDelete(item._id)} />
                            </div>
                        );
                    })}
            </div>
        );
    }),
);

export default LegoItem;

const templateInfo = {
    bgColor: { palette: "Однотонный из палитры", default: "Темный" },
    ctaColor: { palette: "Из палитры", default: "Зеленый" },
    queueDuration: (val) => `${val} секунд${pluralForm(val, ["а", "ы", ""])} `,
    queueRandomness: { true: "Рандомные значения", false: "Близкие значения" },
    isPartnerHeaderVariant: { false: "Стандартный", true: "Стандартный+Partners name" },
};

const statusMap = {
    draft: { label: "Черновик", severity: "gray" },
    active: { label: "Активный", severity: "success" },
    archived: { label: "Архивный", severity: "danger" },
};
